import { useMemo, useState } from 'react';
import type { MemberLensView } from '@catalyst-psa/shared';

import { Card } from '../../components/ui/Card.js';
import { Spinner } from '../../components/ui/Spinner.js';
import { useAuth } from '../../auth/authContext.js';
import { useInsightsMemberOptions, useMemberLens } from './api.js';
import { DonutChart, HBarChart, HeatmapCalendar, StackedBarChart } from './charts.js';
import type { DrillTarget } from './InsightsDrillModal.js';

interface MemberLensProps {
  onDrill: (target: DrillTarget) => void;
}

const MONTH_OPTIONS = [3, 6, 12, 24] as const;

/**
 * "What has this person been doing" — a per-member activity profile: hours heatmap
 * calendar, billable mix, customer/category breakdowns, and a monthly trend.
 */
export function MemberLens({ onDrill }: MemberLensProps): JSX.Element {
  const { principal } = useAuth();
  const optionsQuery = useInsightsMemberOptions(true);
  const [memberId, setMemberId] = useState<string | null>(null);
  const [months, setMonths] = useState<number>(12);
  const [showInactive, setShowInactive] = useState(false);

  const options = useMemo(() => {
    const all = optionsQuery.data ?? [];
    return showInactive ? all : all.filter((m) => m.isActive);
  }, [optionsQuery.data, showInactive]);

  // Default to the signed-in member once options load.
  const effectiveMemberId =
    memberId ??
    (principal && options.some((m) => m.id === principal.id) ? principal.id : options[0]?.id ?? null);

  const lensQuery = useMemberLens(effectiveMemberId, months);
  const lens = lensQuery.data;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <label className="block">
          <span className="mb-1 block text-xs font-medium text-slate-600">Member</span>
          <select
            value={effectiveMemberId ?? ''}
            onChange={(e) => setMemberId(e.target.value || null)}
            className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm"
          >
            {options.map((m) => (
              <option key={m.id} value={m.id}>
                {m.displayName}
                {m.isActive ? '' : ' (inactive)'}
              </option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="mb-1 block text-xs font-medium text-slate-600">Window</span>
          <select
            value={months}
            onChange={(e) => setMonths(Number(e.target.value))}
            className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm"
          >
            {MONTH_OPTIONS.map((m) => (
              <option key={m} value={m}>
                Last {m} months
              </option>
            ))}
          </select>
        </label>
        <label className="mb-1.5 flex items-center gap-1.5 text-xs text-slate-600">
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
            className="rounded border-slate-300"
          />
          Include inactive members
        </label>
      </div>

      {lensQuery.isLoading ? (
        <div className="flex justify-center py-12">
          <Spinner />
        </div>
      ) : null}
      {lensQuery.isError ? (
        <p className="rounded-lg bg-rose-50 p-3 text-sm text-rose-700">
          Could not load this member&rsquo;s activity.
        </p>
      ) : null}

      {lens ? <LensBody lens={lens} onDrill={onDrill} /> : null}
    </div>
  );
}

function LensBody({ lens, onDrill }: { lens: MemberLensView; onDrill: (t: DrillTarget) => void }): JSX.Element {
  const hoursFmt = (v: number): string =>
    `${v.toLocaleString(undefined, { maximumFractionDigits: 1 })} h`;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-6">
        <Stat label="Total hours" value={hoursFmt(lens.totalHours)} />
        <Stat label="Billable hours" value={hoursFmt(lens.billableHours)} />
        <Stat
          label="Billable %"
          value={lens.billablePercent === null ? '—' : `${lens.billablePercent.toFixed(1)}%`}
        />
        <Stat label="Entries" value={lens.entryCount.toLocaleString()} />
        <Stat label="Customers" value={lens.distinctCustomers.toLocaleString()} />
        <Stat label="Tickets" value={lens.distinctTickets.toLocaleString()} />
      </div>

      <Card title="Daily activity" description={`${lens.from} → ${lens.to} · darker = more hours`}>
        <HeatmapCalendar days={lens.days} from={lens.from} to={lens.to} />
      </Card>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card title="Hours by customer">
          <HBarChart
            rows={lens.byCustomer.map((s) => ({
              label: s.label,
              value: s.hours,
              clickable: s.id !== null,
            }))}
            format={hoursFmt}
            onSelect={(i) => {
              const slice = lens.byCustomer[i];
              if (!slice?.id) return;
              onDrill({
                filters: {
                  memberIds: [lens.memberId],
                  customerIds: [slice.id],
                  dateFrom: lens.from,
                  dateTo: lens.to,
                },
                title: `${lens.memberName} · ${slice.label}`,
              });
            }}
          />
          {lens.byCustomer.length === 0 ? (
            <p className="py-4 text-center text-sm text-slate-500">No hours in this window.</p>
          ) : null}
        </Card>
        <Card title="Hours by service category">
          <DonutChart
            slices={lens.byServiceCategory.map((s) => ({ label: s.label, value: s.hours }))}
            format={hoursFmt}
            onSelect={(i) => {
              const slice = lens.byServiceCategory[i];
              if (!slice?.id) return;
              onDrill({
                filters: {
                  memberIds: [lens.memberId],
                  serviceCategoryIds: [slice.id],
                  dateFrom: lens.from,
                  dateTo: lens.to,
                },
                title: `${lens.memberName} · ${slice.label}`,
              });
            }}
          />
        </Card>
      </div>

      <Card title="Monthly trend" description="Billable vs non-billable hours per month">
        <StackedBarChart
          categories={lens.byMonth.map((m) => m.month)}
          series={[
            { label: 'Billable', values: lens.byMonth.map((m) => m.billableHours) },
            { label: 'Non-billable', values: lens.byMonth.map((m) => m.hours - m.billableHours) },
          ]}
          format={hoursFmt}
          onSelect={(ci) => {
            const month = lens.byMonth[ci];
            if (!month) return;
            onDrill({
              filters: {
                memberIds: [lens.memberId],
                dateFrom: `${month.month}-01`,
                dateTo: endOfMonth(month.month),
              },
              title: `${lens.memberName} · ${month.month}`,
            });
          }}
        />
      </Card>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
      <p className="text-xs text-slate-500">{label}</p>
      <p className="mt-1 text-lg font-semibold tabular-nums text-slate-900">{value}</p>
    </div>
  );
}

function endOfMonth(yearMonth: string): string {
  const year = Number(yearMonth.slice(0, 4));
  const month = Number(yearMonth.slice(5, 7));
  return new Date(Date.UTC(year, month, 0)).toISOString().slice(0, 10);
}
